import {
  Button,
  CustomFilter,
  DatePicker,
  EllipsisSpan,
  Form,
  Modal,
  Paging,
  Table,
  Wrapper,
  message,
  MultiSelect,
  useBreadcrumbRoutes,
  usePaging,
  useUpdate,
} from '@maxtropy/components';
import { Space, Upload } from 'antd';
import React, { useState } from 'react';
import dayjs from 'dayjs';
import styles from './index.module.scss';
import {
  apiV2MeterReadingAreaListPost,
  apiV2MeterReadingDataPropertyListPost,
  apiV2MeterReadingDeviceListPost,
  apiV2MeterReadingImportRecordDeletePost,
  apiV2MeterReadingImportRecordQueryPost,
} from '@maxtropy/device-customer-apis-v2';
import { useRequest } from 'ahooks';
import { UploadChangeParam } from 'antd/es/upload';
import { UploadFile } from 'antd/es/upload/interface';
// 筛选条件类型
export interface FilterParams {
  areaIds?: number[];
  deviceIds?: number[];
  dataPropertyIds?: number[];
  importDataTime?: [dayjs.Dayjs, dayjs.Dayjs];
}

// 表格列配置
const columns = [
  {
    title: '站房名称',
    dataIndex: 'areaName',
    ellipsis: true,
    render: (v: string) => <EllipsisSpan value={v} />,
  },
  {
    title: '设备名称',
    dataIndex: 'deviceName',
    ellipsis: true,
    render: (v: string) => <EllipsisSpan value={v} />,
  },
  {
    title: '数据属性',
    dataIndex: 'dataPropertyName',
    ellipsis: true,
    render: (v: string) => <EllipsisSpan value={v} />,
  },
  {
    title: '导入数据',
    dataIndex: 'value',
    ellipsis: true,
    render: (v: string) => <EllipsisSpan value={v} />,
  },
  {
    title: '导入数据时间',
    dataIndex: 'readingTime',
    ellipsis: true,
    render: (v: string) => <EllipsisSpan value={dayjs(v).format('YYYY-MM-DD HH:mm:ss')} />,
  },
  {
    title: '操作时间',
    dataIndex: 'createTime',
    ellipsis: true,
    render: (v: string) => <EllipsisSpan value={dayjs(v).format('YYYY-MM-DD HH:mm:ss')} />,
  },
  {
    title: '操作人',
    dataIndex: 'operator',
    ellipsis: true,
    render: (v: string) => <EllipsisSpan value={v} />,
  },
];

const ManualMeterReadingImport: React.FC = () => {
  const breadcrumbRoutes = useBreadcrumbRoutes();
  const [form] = Form.useForm();
  const [templateForm] = Form.useForm();
  const [isTemplateModalVisible, setIsTemplateModalVisible] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([]);
  const [searchParams, setSearchParams] = useState<FilterParams>({});
  const pagingInfo = usePaging(50);
  const { pageOffset, pageSize, setTotalCount, setPageOffset } = pagingInfo;
  const [updateState, updateFn] = useUpdate();
  const deviceId = Form.useWatch('deviceIds', form);
  const areaId = Form.useWatch('areaIds', form);

  //获取站房名称列表
  const { data: StationData } = useRequest(() =>
    apiV2MeterReadingAreaListPost({}).then(res => {
      const areaList = res.list?.map(item => ({ label: item.name, value: item.id })) || [];
      return areaList;
    })
  );

  //获取设备列表
  const { data: DeviceData } = useRequest(
    () =>
      apiV2MeterReadingDeviceListPost({ areaIds: areaId }).then(res => {
        const deviceList = res.list?.map(device => ({ label: device.name, value: device.id }));
        return deviceList;
      }),
    {
      refreshDeps: [areaId],
    }
  );
  //获取数据点
  const { data: PropertyData } = useRequest(
    () =>
      apiV2MeterReadingDataPropertyListPost({ deviceIds: deviceId }).then(allDataRes => {
        const flatDataProperties = allDataRes.list?.map(property => ({ label: property.name, value: property.id }));
        return flatDataProperties;
      }),
    {
      ready: !!deviceId,
      refreshDeps: [deviceId],
    }
  );

  // 数据请求
  const { data: tableData, loading } = useRequest(
    () =>
      apiV2MeterReadingImportRecordQueryPost({
        areaIds: searchParams.areaIds,
        deviceIds: searchParams.deviceIds,
        dataPropertyIds: searchParams.dataPropertyIds,
        readingTimeStart: searchParams.importDataTime?.[0]?.startOf('day').toISOString(),
        readingTimeEnd: searchParams.importDataTime?.[1]?.endOf('day').toISOString(),
        page: pageOffset,
        size: pageSize,
      }).then(res => {
        setTotalCount(res.total!);
        return res.list || [];
      }),

    {
      refreshDeps: [pageOffset, pageSize, searchParams, updateState],
    }
  );

  // 下载导入模板
  const handleDownloadTemplate = () => {
    templateForm.resetFields();
    setIsTemplateModalVisible(true);
  };

  // 下载模板确认
  const handleDownloadConfirm = () => {
    templateForm.validateFields().then(values => {
      const { areaIds } = values;
      const urlIds = areaIds.map((id: number) => `areaIds=${id}`).join('&');
      window.open(`/api/v2/meter-reading/template/download?${urlIds}`);
      setIsTemplateModalVisible(false);
    });
  };

  // 批量删除
  const handleBatchDelete = () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请先选择要删除的数据');
      return;
    }
    Modal.confirm({
      title: '确认删除',
      content: `确定要删除选中的 ${selectedRowKeys.length} 条数据吗？删除后不可恢复。`,
      onOk: () => {
        apiV2MeterReadingImportRecordDeletePost({ recordIds: selectedRowKeys }).then(() => {
          message.success('删除成功');
          setSelectedRowKeys([]);
          updateFn();
        });
      },
    });
  };

  // 删除单条数据
  const handleDelete = (record: any) => {
    Modal.confirm({
      title: '确认删除',
      content: `确定要删除这条数据吗？删除后不可恢复。`,
      onOk: () => {
        apiV2MeterReadingImportRecordDeletePost({ recordIds: [record.id] }).then(() => {
          message.success('删除成功');
          updateFn();
        });
      },
    });
  };

  const buildColumns = [
    ...columns,
    {
      title: '操作',
      key: 'action',
      width: 80,
      fixed: 'right' as const,
      render: (_: unknown, record: any) => (
        <Button type="link" danger onClick={() => handleDelete(record)}>
          删除
        </Button>
      ),
    },
  ];

  // 筛选条件
  const onFinish = (values: FilterParams) => {
    setPageOffset(1);
    setSearchParams(values);
  };

  const onReset = () => {
    setPageOffset(1);
    setSearchParams({});
    form.resetFields();
  };

  const filters = (
    <CustomFilter<FilterParams> form={form} onFinish={onFinish} onReset={onReset}>
      <Form.Item name="areaIds" label="站房名称">
        <MultiSelect
          showSearch
          placeholder="请选择站房名称"
          options={StationData}
          optionFilterProp="label"
          allowClear
          filterOption={(input, option) => (option?.label as string)?.toLowerCase().includes(input.toLowerCase())}
        />
      </Form.Item>
      <Form.Item name="deviceIds" label="设备名称">
        <MultiSelect
          showSearch
          placeholder="请选择设备名称"
          options={DeviceData}
          optionFilterProp="label"
          allowClear
          filterOption={(input, option) => (option?.label as string)?.toLowerCase().includes(input.toLowerCase())}
        />
      </Form.Item>
      <Form.Item name="dataPropertyIds" label="数据属性">
        <MultiSelect
          showSearch
          placeholder="请选择数据属性"
          options={PropertyData}
          optionFilterProp="label"
          allowClear
          filterOption={(input, option) => (option?.label as string)?.toLowerCase().includes(input.toLowerCase())}
        />
      </Form.Item>
      <Form.Item name="importDataTime" label="导入数据时间">
        <DatePicker.RangePicker format="YYYY-MM-DD" />
      </Form.Item>
    </CustomFilter>
  );

  // 行选择配置
  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys: React.Key[]) => {
      setSelectedRowKeys(newSelectedRowKeys as number[]);
    },
    preserveSelectedRowKeys: true,
  };

  return (
    <Wrapper routes={breadcrumbRoutes?.routes} filters={filters} className={styles.wrapper}>
      <div className={styles.headerActions}>
        <Space size="small">
          <Button type="primary" onClick={handleDownloadTemplate}>
            下载导入模板
          </Button>
          <Upload
            action="/api/v2/meter-reading/data/import"
            accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            showUploadList={false}
            onChange={(val: UploadChangeParam<UploadFile>) => {
              if (val.file.status === 'error') {
                Modal.error({
                  title: '导入失败！',
                });
              }
              if (val.file.status === 'done') {
                const response = val.file.response;
                if (response && response.code === 0) {
                  Modal.success({
                    title: '导入成功！',
                  });
                  updateFn();
                } else {
                  Modal.error({
                    title: response?.msg || '导入失败!',
                  });
                }
              }
            }}
          >
            <Button type="primary">导入数据</Button>
          </Upload>
          <Button type="primary" onClick={handleBatchDelete}>
            批量删除
          </Button>
        </Space>
      </div>
      <Table
        rowKey="id"
        sticky
        loading={loading}
        columns={buildColumns}
        dataSource={tableData}
        rowSelection={rowSelection}
        scroll={{ x: 1400 }}
      />
      <Paging pagingInfo={pagingInfo} />

      {/* 下载导入模板弹窗 */}
      <Modal
        contentClassName="modal-form-content"
        title="下载导入模板"
        open={isTemplateModalVisible}
        onOk={handleDownloadConfirm}
        onCancel={() => setIsTemplateModalVisible(false)}
        destroyOnClose
        width={500}
        okText="下载"
      >
        <Form form={templateForm} labelCol={{ span: 6 }}>
          <Form.Item name="areaIds" label="站房名称" rules={[{ required: true, message: '请选择站房名称' }]}>
            <MultiSelect
              showSearch
              placeholder="请选择站房名称"
              options={StationData}
              optionFilterProp="label"
              allowClear
              filterOption={(input, option) => (option?.label as string)?.toLowerCase().includes(input.toLowerCase())}
            />
          </Form.Item>
        </Form>
      </Modal>
    </Wrapper>
  );
};

export default ManualMeterReadingImport;
